import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CreditCard, Landmark, FileText, CheckCircle2 } from 'lucide-react';
import { billingApi } from '../lib/api';
import { useCompany } from '../contexts/CompanyContext';
import { getTrialDaysRemaining } from '../lib/trial';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const CYCLE_LABELS = { monthly: 'мес.', quarterly: 'квартал', yearly: 'год' };
const STATUS_LABELS = {
  trial: 'Пробный период',
  active: 'Активна',
  suspended: 'Приостановлена',
  cancelled: 'Отменена',
  frozen: 'Заморожена',
};
const STATUS_VARIANTS = {
  trial: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-red-100 text-red-800',
  frozen: 'bg-gray-200 text-gray-800',
};

const PAYMENT_METHODS = [
  { value: 'card', label: 'Банковская карта', icon: CreditCard },
  { value: 'sbp', label: 'СБП', icon: Landmark },
  { value: 'invoice', label: 'Счёт для юрлица', icon: FileText },
];

export default function Billing() {
  const { currentCompany, selectCompany } = useCompany();
  const queryClient = useQueryClient();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardLast4, setCardLast4] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: plansData } = useQuery({
    queryKey: ['billing-plans'],
    queryFn: () => billingApi.getPlans(),
  });
  const { data: subscriptionData } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => billingApi.getSubscription(),
  });
  const { data: usageData } = useQuery({
    queryKey: ['billing-usage'],
    queryFn: () => billingApi.getUsage(),
  });
  const { data: invoicesData } = useQuery({
    queryKey: ['billing-invoices'],
    queryFn: () => billingApi.getInvoices(),
  });

  const plans = plansData?.data?.plans || [];
  const subscription = subscriptionData?.data?.subscription;
  const usage = usageData?.data?.usage;
  const invoices = invoicesData?.data?.invoices || [];
  const trialDaysRemaining = getTrialDaysRemaining(currentCompany);

  const priceField = { monthly: 'monthly_price', quarterly: 'quarterly_price', yearly: 'yearly_price' }[billingCycle];

  const handleCheckout = async () => {
    if (!checkoutPlan) return;
    setSubmitting(true);
    try {
      const response = await billingApi.checkout({
        plan_id: checkoutPlan.id,
        billing_cycle: billingCycle,
        payment_method: paymentMethod,
        card_last4: paymentMethod === 'card' ? cardLast4.slice(-4) : undefined,
      });

      if (response.data.company) {
        selectCompany(response.data.company);
      }
      queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      queryClient.invalidateQueries({ queryKey: ['billing-invoices'] });

      toast.success('Оплата прошла успешно — подписка активна');
      setCheckoutPlan(null);
      setCardLast4('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось оформить подписку');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    try {
      await billingApi.cancelSubscription();
      queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
      toast.success('Подписка отменена');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось отменить подписку');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Биллинг</h1>
        <p className="text-gray-600">Тариф, оплата и история платежей компании</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Текущая подписка</CardTitle>
              {currentCompany?.subscription_status && (
                <Badge className={STATUS_VARIANTS[currentCompany.subscription_status] || ''}>
                  {STATUS_LABELS[currentCompany.subscription_status] || currentCompany.subscription_status}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-gray-600">
              Тариф: <span className="font-medium text-gray-900">{currentCompany?.subscription_plan || 'free'}</span>
            </p>
            {trialDaysRemaining !== null && (
              <p className="text-sm text-gray-600">
                {trialDaysRemaining > 0
                  ? `Пробный период: осталось ${trialDaysRemaining} дн.`
                  : 'Пробный период закончился'}
              </p>
            )}
            {currentCompany?.subscription_ends_at && currentCompany.subscription_status === 'active' && (
              <p className="text-sm text-gray-600">
                Следующее списание: {new Date(currentCompany.subscription_ends_at).toLocaleDateString('ru-RU')}
              </p>
            )}
            {currentCompany?.subscription_status === 'active' && (
              <Button variant="outline" size="sm" onClick={handleCancel} data-testid="cancel-subscription-button">
                Отменить подписку
              </Button>
            )}
          </CardContent>
        </Card>

        {usage && (
          <Card>
            <CardHeader>
              <CardTitle>Использование</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Пользователи</span>
                  <span>{usage.users.current} / {usage.users.limit}</span>
                </div>
                <Progress value={Math.min(usage.users.percentage, 100)} />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Проекты</span>
                  <span>{usage.projects.current} / {usage.projects.limit}</span>
                </div>
                <Progress value={Math.min(usage.projects.percentage, 100)} />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Тарифы</h2>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {Object.entries(CYCLE_LABELS).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setBillingCycle(value)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  billingCycle === value ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
                data-testid={`billing-cycle-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const isCurrent = currentCompany?.subscription_plan === plan.plan_type && currentCompany?.subscription_status === 'active';
            return (
              <Card key={plan.id} className={isCurrent ? 'border-blue-500 border-2' : ''}>
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-gray-900 mb-3">
                    {(plan[priceField] || 0).toLocaleString('ru-RU')} ₽
                    <span className="text-sm font-normal text-gray-500"> / {CYCLE_LABELS[billingCycle]}</span>
                  </p>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {(plan.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  {isCurrent ? (
                    <Button className="w-full" disabled variant="outline">Текущий тариф</Button>
                  ) : (
                    <Button
                      className="w-full"
                      onClick={() => setCheckoutPlan(plan)}
                      data-testid={`select-plan-${plan.plan_type}`}
                    >
                      Выбрать
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>

      {invoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>История платежей</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Дата</TableHead>
                  <TableHead>Тариф</TableHead>
                  <TableHead>Способ оплаты</TableHead>
                  <TableHead>Сумма</TableHead>
                  <TableHead>Статус</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{new Date(inv.created_at).toLocaleDateString('ru-RU')}</TableCell>
                    <TableCell>{inv.plan_id}</TableCell>
                    <TableCell>{PAYMENT_METHODS.find((m) => m.value === inv.payment_method)?.label || inv.payment_method}</TableCell>
                    <TableCell>{(inv.amount || 0).toLocaleString('ru-RU')} ₽</TableCell>
                    <TableCell>
                      <Badge className={inv.status === 'paid' ? STATUS_VARIANTS.active : STATUS_VARIANTS.suspended}>
                        {inv.status === 'paid' ? 'Оплачен' : inv.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!checkoutPlan} onOpenChange={(open) => !open && setCheckoutPlan(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Оформление тарифа «{checkoutPlan?.name}»</DialogTitle>
            <DialogDescription>
              {(checkoutPlan?.[priceField] || 0).toLocaleString('ru-RU')} ₽ / {CYCLE_LABELS[billingCycle]}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Способ оплаты</Label>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                {PAYMENT_METHODS.map(({ value, label, icon: Icon }) => (
                  <div key={value} className="flex items-center gap-2 py-1">
                    <RadioGroupItem value={value} id={`pm-${value}`} data-testid={`payment-method-${value}`} />
                    <Icon className="h-4 w-4 text-gray-500" />
                    <Label htmlFor={`pm-${value}`} className="cursor-pointer font-normal">{label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {paymentMethod === 'card' && (
              <div>
                <Label htmlFor="card-number">Номер карты</Label>
                <Input
                  id="card-number"
                  placeholder="0000 0000 0000 0000"
                  value={cardLast4}
                  onChange={(e) => setCardLast4(e.target.value)}
                  data-testid="card-number-input"
                />
                <p className="text-xs text-gray-500 mt-1">Демо-режим — реальная карта не списывается.</p>
              </div>
            )}
            {paymentMethod === 'sbp' && (
              <p className="text-sm text-gray-600">После подтверждения появится QR-код для оплаты через приложение банка (в демо-режиме подтверждается сразу).</p>
            )}
            {paymentMethod === 'invoice' && (
              <p className="text-sm text-gray-600">
                Счёт будет выставлен на {currentCompany?.legal_name || currentCompany?.name}
                {currentCompany?.inn ? ` (ИНН ${currentCompany.inn})` : ''}. В демо-режиме считается оплаченным сразу.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutPlan(null)} disabled={submitting}>
              Отмена
            </Button>
            <Button onClick={handleCheckout} disabled={submitting} data-testid="confirm-checkout-button">
              {submitting ? 'Оплачиваем...' : 'Оплатить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
